import { Link } from 'react-router-dom';
import { ArrowRight, Heart, Users, Sparkles, Target } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function AboutPage() {
  return (
    <main className="pt-20 md:pt-24 bg-[#FDFCF8]" data-testid="about-page">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-[#F5F5F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <span className="text-sm font-medium tracking-widest uppercase text-[#4A5D4F] mb-4 block">
              Über uns
            </span>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-medium text-[#2C332E] mb-6 leading-tight">
              Eid Geschenke{' '}
              <span className="text-[#4A5D4F] italic">einfach</span>,{' '}
              bedeutungsvoll und{' '}
              <span className="text-[#4A5D4F] italic">schön</span> machen
            </h1>
            <p className="text-lg md:text-xl text-[#6B7280] leading-relaxed">
              Wir glauben, dass ein Geschenk mehr ist als nur ein Gegenstand – 
              es ist ein Ausdruck von Liebe, Wertschätzung und Verbundenheit.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <span className="text-sm font-medium tracking-widest uppercase text-[#4A5D4F] mb-4 block">
                Unsere Mission
              </span>
              <h2 className="font-serif text-3xl md:text-4xl font-medium text-[#2C332E] mb-6">
                Warum EidGeschenke.de?
              </h2>
              <div className="space-y-4 text-[#6B7280] leading-relaxed">
                <p>
                  Die Suche nach dem perfekten Eid-Geschenk kann überwältigend sein. 
                  Zwischen unzähligen Online-Shops und endlosen Produktlisten verliert 
                  man schnell den Überblick – und die Freude am Schenken.
                </p>
                <p>
                  Genau hier setzen wir an: EidGeschenke.de ist eine handverlesene 
                  Plattform, die Ihnen die Suche abnimmt. Wir wählen sorgfältig 
                  Geschenke aus, die nicht nur schön, sondern auch bedeutungsvoll sind.
                </p>
                <p>
                  Unser Fokus liegt auf Qualität statt Quantität. Wir empfehlen nur 
                  Produkte, von denen wir überzeugt sind – damit Sie mit gutem 
                  Gewissen schenken können.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="rounded-3xl overflow-hidden shadow-xl bg-gradient-to-br from-[#4A5D4F] to-[#3A493E] aspect-[4/5] flex items-center justify-center">
                {/* Decorative circles */}
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute top-8 right-8 w-20 h-20 border-2 border-[#D4C5A9] rounded-full" />
                  <div className="absolute bottom-12 left-8 w-28 h-28 border-2 border-[#D4C5A9] rounded-full" />
                </div>
                
                {/* Content */}
                <div className="relative text-center p-8">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#D4C5A9]/20 flex items-center justify-center">
                    <Heart className="w-8 h-8 text-[#D4C5A9]" />
                  </div>
                  <p className="text-[#D4C5A9] font-serif text-xl mb-2">Mit Liebe</p>
                  <p className="text-white/60 text-sm">Für dich ausgewählt</p>
                </div>
              </div>
              {/* Floating Card */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-6 shadow-xl max-w-[220px]">
                <Heart className="w-8 h-8 text-[#D4C5A9] mb-3" />
                <p className="font-serif text-lg font-medium text-[#2C332E] mb-1">
                  Mit Herz ausgewählt
                </p>
                <p className="text-sm text-[#6B7280]">
                  Jedes Geschenk persönlich ausgewählt
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 md:py-24 bg-[#F5F5F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-sm font-medium tracking-widest uppercase text-[#4A5D4F] mb-4 block">
              Unsere Werte
            </span>
            <h2 className="font-serif text-3xl md:text-4xl font-medium text-[#2C332E] mb-4">
              Wofür wir stehen
            </h2>
            <p className="text-lg text-[#6B7280] max-w-2xl mx-auto">
              Drei Prinzipien leiten unsere Arbeit und die Auswahl unserer 
              Geschenkempfehlungen.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Sparkles,
                title: 'Kuratierung',
                description:
                  'Wir wählen jedes Geschenk sorgfältig aus. Keine Massenware, keine beliebigen Produkte – nur das, was wir selbst verschenken würden.',
              },
              {
                icon: Target,
                title: 'Vertrauen',
                description:
                  'Transparenz ist uns wichtig. Wir kennzeichnen Partnerlinks und empfehlen nur Produkte von etablierten, vertrauenswürdigen Anbietern.',
              },
              {
                icon: Users,
                title: 'Gemeinschaft',
                description:
                  'Eid ist ein Fest der Gemeinschaft. Unsere Geschenkideen sollen Menschen zusammenbringen und Freude in Familien tragen.',
              },
            ].map((value, index) => (
              <div
                key={index}
                className="p-8 rounded-3xl bg-white shadow-soft hover:shadow-lg transition-all duration-300"
                data-testid={`value-${index}`}
              >
                <div className="w-14 h-14 rounded-2xl bg-[#4A5D4F]/10 flex items-center justify-center mb-6">
                  <value.icon className="w-7 h-7 text-[#4A5D4F]" />
                </div>
                <h3 className="font-serif text-xl font-medium text-[#2C332E] mb-4">
                  {value.title}
                </h3>
                <p className="text-[#6B7280] leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Important Notice */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="bg-[#4A5D4F] rounded-3xl p-8 md:p-12 lg:p-16 text-center">
            <h2 className="font-serif text-2xl md:text-3xl font-medium text-white mb-6">
              Wichtiger Hinweis
            </h2>
            <p className="text-white/80 leading-relaxed max-w-2xl mx-auto mb-8">
              EidGeschenke.de ist keine Online-Shop. Wir verkaufen keine Produkte 
              direkt, sondern empfehlen sorgfältig ausgewählte Geschenkideen von 
              vertrauenswürdigen Partner-Shops. Einige Links auf dieser Seite sind 
              Affiliate-Links – bei einem Kauf über diese Links erhalten wir eine 
              kleine Provision, ohne dass für Sie Mehrkosten entstehen.
            </p>
            <Button
              asChild
              className="bg-white text-[#4A5D4F] hover:bg-[#F5F5F0] rounded-full px-8 py-6 text-base font-medium"
            >
              <Link to="/geschenke" data-testid="about-cta">
                Geschenke entdecken
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 md:py-20 bg-[#F5F5F0]">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2C332E] mb-4">
            Haben Sie Fragen?
          </h2>
          <p className="text-[#6B7280] mb-8">
            Wir freuen uns über Ihr Feedback und Ihre Anregungen. 
            Schreiben Sie uns gerne!
          </p>
          <p className="text-[#4A5D4F] font-medium">
            kontakt@eidgeschenke.de
          </p>
        </div>
      </section>
    </main>
  );
}
